/**
 * Customer ratings of service orders: summary, filters and moderation.
 */
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Rating,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Star as StarIcon,
  Reviews as ReviewsIcon,
  VisibilityOff as HiddenIcon,
  Visibility as VisibleIcon,
  Percent as PercentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import moment from 'moment';
import { DataTable, PageHeader, StatsCard, type Column } from '../../components/common';
import { usePermissions } from '../../hooks/usePermissions';
import {
  useGetReviewsQuery,
  useGetReviewStatisticsQuery,
  useUpdateReviewMutation,
  type ReviewsParams,
} from '../../store/api/reviewsApi';
import type { OrderReview } from '../../types/review';

const ReviewsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('manage_reviews');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [visibility, setVisibility] = useState('');
  const [source, setSource] = useState('');

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const params: ReviewsParams = { page: page + 1, page_size: rowsPerPage };
  if (search) params.search = search;
  if (ratingFilter === 'low') params.max_rating = 3;
  else if (ratingFilter) params.rating = Number(ratingFilter);
  if (visibility) params.is_hidden = visibility === 'hidden';
  if (source) params.source = source as ReviewsParams['source'];

  const { data, isLoading, isFetching } = useGetReviewsQuery(params);
  const { data: statsData } = useGetReviewStatisticsQuery();
  const stats = statsData?.data;
  const [updateReview] = useUpdateReviewMutation();

  const toggleHidden = async (review: OrderReview) => {
    try {
      await updateReview({ id: review.id, is_hidden: !review.is_hidden }).unwrap();
      toast.success(review.is_hidden ? t('reviews.toast.shown') : t('reviews.toast.hidden'));
    } catch {
      toast.error(t('reviews.toast.updateFailed'));
    }
  };

  const columns: Column<OrderReview>[] = [
    {
      id: 'order_number',
      label: t('reviews.cols.order'),
      render: (r) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {r.order_number}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {r.subservice_name || r.service_name}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'patient_name',
      label: t('reviews.cols.customer'),
      render: (r) => (
        <Box>
          <Typography variant="body2">{r.customer_name || r.patient_name}</Typography>
          <Typography variant="caption" color="text.secondary" dir="ltr">
            {r.customer_phone}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'service_rating',
      label: t('reviews.cols.service'),
      render: (r) => <Rating value={r.service_rating} readOnly size="small" />,
    },
    {
      id: 'provider_rating',
      label: t('reviews.cols.provider'),
      render: (r) =>
        r.provider ? (
          <Box>
            <Typography variant="caption">{r.provider.name}</Typography>
            {r.provider_rating != null ? (
              <Rating value={r.provider_rating} readOnly size="small" sx={{ display: 'flex' }} />
            ) : (
              <Typography variant="caption" color="text.secondary" display="block">
                —
              </Typography>
            )}
          </Box>
        ) : (
          '—'
        ),
    },
    {
      id: 'comment',
      label: t('reviews.cols.comment'),
      minWidth: 220,
      render: (r) =>
        r.comment ? (
          <Tooltip title={r.comment}>
            <Typography variant="body2" sx={{ maxWidth: 280 }} noWrap>
              {r.comment}
            </Typography>
          </Tooltip>
        ) : (
          '—'
        ),
    },
    {
      id: 'source',
      label: t('reviews.cols.source'),
      render: (r) => (
        <Chip
          size="small"
          variant="outlined"
          color={r.source === 'APP' ? 'primary' : 'default'}
          label={t(`reviews.source.${r.source}`)}
        />
      ),
    },
    {
      id: 'created_at',
      label: t('reviews.cols.date'),
      render: (r) => moment(r.created_at).format('DD MMM YYYY'),
    },
    {
      id: 'is_hidden',
      label: t('reviews.cols.status'),
      // One control per row: the action button for managers, the status for everyone else
      render: (r) =>
        canManage ? (
          <Button
            size="small"
            variant={r.is_hidden ? 'contained' : 'outlined'}
            color={r.is_hidden ? 'success' : 'warning'}
            startIcon={r.is_hidden ? <VisibleIcon fontSize="small" /> : <HiddenIcon fontSize="small" />}
            sx={{ whiteSpace: 'nowrap' }}
            onClick={(e) => {
              e.stopPropagation();
              toggleHidden(r);
            }}
          >
            {r.is_hidden ? t('reviews.actions.show') : t('reviews.actions.hide')}
          </Button>
        ) : (
          <Chip
            size="small"
            icon={r.is_hidden ? <HiddenIcon fontSize="small" /> : <VisibleIcon fontSize="small" />}
            label={r.is_hidden ? t('reviews.status.hidden') : t('reviews.status.visible')}
            color={r.is_hidden ? 'warning' : 'success'}
            variant="outlined"
          />
        ),
    },
  ];

  const ratedPct =
    stats && stats.completed_orders
      ? Math.round((stats.rated_completed_orders / stats.completed_orders) * 100)
      : 0;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('reviews.title')}
        subtitle={t('reviews.subtitle')}
        breadcrumbs={[{ label: t('nav.dashboard'), path: '/' }, { label: t('reviews.title') }]}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <StatsCard
          title={t('reviews.stats.averageService')}
          value={stats ? `${stats.average_service_rating.toFixed(1)} / 5` : '—'}
          icon={<StarIcon />}
          color="warning"
        />
        <StatsCard
          title={t('reviews.stats.averageProvider')}
          value={stats ? `${stats.average_provider_rating.toFixed(1)} / 5` : '—'}
          icon={<StarIcon />}
          color="info"
        />
        <StatsCard
          title={t('reviews.stats.total')}
          value={stats?.total_reviews ?? 0}
          icon={<ReviewsIcon />}
          color="primary"
        />
        <StatsCard
          title={t('reviews.stats.ratedPct')}
          value={`${ratedPct}%`}
          icon={<PercentIcon />}
          color="success"
        />
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t('reviews.filters.search')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('reviews.filters.rating')}</InputLabel>
            <Select
              label={t('reviews.filters.rating')}
              value={ratingFilter}
              onChange={(e) => {
                setRatingFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('reviews.filters.all')}</MenuItem>
              <MenuItem value="low">{t('reviews.filters.lowRatings')}</MenuItem>
              {[5, 4, 3, 2, 1].map((n) => (
                <MenuItem key={n} value={String(n)}>
                  {'★'.repeat(n)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('reviews.filters.visibility')}</InputLabel>
            <Select
              label={t('reviews.filters.visibility')}
              value={visibility}
              onChange={(e) => {
                setVisibility(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('reviews.filters.all')}</MenuItem>
              <MenuItem value="visible">{t('reviews.status.visible')}</MenuItem>
              <MenuItem value="hidden">{t('reviews.status.hidden')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('reviews.filters.source')}</InputLabel>
            <Select
              label={t('reviews.filters.source')}
              value={source}
              onChange={(e) => {
                setSource(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('reviews.filters.all')}</MenuItem>
              <MenuItem value="APP">{t('reviews.source.APP')}</MenuItem>
              <MenuItem value="DASHBOARD">{t('reviews.source.DASHBOARD')}</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <Card>
        <DataTable
          columns={columns}
          data={data?.results ?? []}
          loading={isLoading || isFetching}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.count ?? 0}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          emptyMessage={t('reviews.empty')}
          onRowClick={(r) => navigate(`/orders/${r.order}`)}
        />
      </Card>
    </Box>
  );
};

export default ReviewsListPage;
